import { useState, useRef, useEffect } from 'react'
import { Folder, Play, Pause, Scissors } from 'lucide-react'
import { Card } from './ui/Card'
import { useI18n } from '../utils/i18n'

/**
 * 源音频概览组件
 * 显示完整音频波形，标注所有切分点，支持拖拽调整
 */
export function SourceAudioOverview({ sourceAudio, segments, onReSplit, onSegmentTimeChange }) {
  const { tx } = useI18n()
  const [waveform, setWaveform] = useState([])
  const [duration, setDuration] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [loading, setLoading] = useState(true)
  const [draggingIndex, setDraggingIndex] = useState(null) // 正在拖拽的切分点索引
  
  const audioRef = useRef(null)
  const audioContextRef = useRef(null)
  const waveformRef = useRef(null)

  useEffect(() => {
    if (sourceAudio) {
      loadWaveform(sourceAudio)
    }
  }, [sourceAudio])

  const loadWaveform = async (url) => {
    setLoading(true)
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)()
      }
      const response = await fetch(url)
      const arrayBuffer = await response.arrayBuffer()
      const audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer)
      const channelData = audioBuffer.getChannelData(0)
      
      // 生成波形数据（300个采样点，更宽的显示）
      const samples = 300
      const blockSize = Math.floor(channelData.length / samples)
      const peaks = []
      
      for (let i = 0; i < samples; i++) {
        let sum = 0
        for (let j = 0; j < blockSize; j++) {
          sum += Math.abs(channelData[i * blockSize + j])
        }
        peaks.push(sum / blockSize)
      }
      
      const max = Math.max(...peaks)
      setWaveform(peaks.map(p => p / max))
      setDuration(audioBuffer.duration)
    } catch (e) {
      console.error('Failed to load waveform:', e)
    }
    setLoading(false)
  }

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime)
    }
  }

  const togglePlay = () => {
    if (!audioRef.current) return
    if (isPlaying) {
      audioRef.current.pause()
    } else {
      audioRef.current.play()
    }
    setIsPlaying(!isPlaying)
  }

  const handleWaveformClick = (e) => {
    if (!duration || !audioRef.current || draggingIndex !== null) return
    const rect = waveformRef.current.getBoundingClientRect()
    const x = e.clientX - rect.left
    const percent = x / rect.width
    const time = percent * duration
    audioRef.current.currentTime = time
    setCurrentTime(time)
  }

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // 用 ref 保存最新值，避免闭包问题
  const draggingIndexRef = useRef(null)
  const segmentsRef = useRef(segments)
  const durationRef = useRef(duration)
  const onChangeRef = useRef(onSegmentTimeChange)
  
  useEffect(() => {
    draggingIndexRef.current = draggingIndex
    segmentsRef.current = segments
    durationRef.current = duration
    onChangeRef.current = onSegmentTimeChange
  }, [draggingIndex, segments, duration, onSegmentTimeChange])

  // 拖拽切分点
  const handleDragStart = (e, index) => {
    e.preventDefault()
    e.stopPropagation()
    if (index === 0) return // 第一个切分点固定在0
    setDraggingIndex(index)
  }

  // 全局鼠标事件监听
  useEffect(() => {
    const handleMouseMove = (e) => {
      const idx = draggingIndexRef.current
      if (idx === null || !waveformRef.current || !durationRef.current) return
      
      const rect = waveformRef.current.getBoundingClientRect()
      const x = e.clientX - rect.left
      const percent = Math.max(0, Math.min(1, x / rect.width))
      const newTime = percent * durationRef.current
      const segs = segmentsRef.current
      
      // 限制在前后切分点之间
      const prevTime = idx > 0 ? (segs[idx - 1]?.start_time || 0) + 0.5 : 0.5
      const nextTime = idx < segs.length - 1 
        ? (segs[idx + 1]?.start_time || durationRef.current) - 0.5 
        : durationRef.current - 0.5
      
      const clampedTime = Math.max(prevTime, Math.min(nextTime, newTime))
      
      if (onChangeRef.current) {
        onChangeRef.current(idx, clampedTime)
      }
    }
    
    const handleMouseUp = () => {
      setDraggingIndex(null)
    }
    
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [])

  // 空格键播放/暂停
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code === 'Space' && sourceAudio && e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA') {
        e.preventDefault()
        togglePlay()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [sourceAudio, isPlaying])

  if (!sourceAudio) return null

  return (
    <Card className="mb-6 p-5">
      <audio
        ref={audioRef}
        src={sourceAudio}
        onTimeUpdate={handleTimeUpdate}
        onEnded={() => setIsPlaying(false)}
        className="hidden"
      />

      {/* 标题栏 */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-800 font-extrabold flex items-center gap-2">
            <Folder size={16} className="text-slate-500" />
            {tx({ zh: '原始音频', en: 'Source audio' })}
          </span>
          <span className="text-xs text-slate-500 font-mono font-bold">{formatTime(duration)}</span>
          <span className="text-xs text-emerald-700 font-bold">
            {tx({ zh: '{n} 个段落', en: '{n} segments' }, { n: segments?.length || 0 })}
          </span>
        </div>
        <button
          onClick={onReSplit}
          className="flex items-center gap-1.5 px-3 py-2 rounded-2xl bg-amber-500 hover:bg-amber-600 text-white text-xs font-extrabold transition-colors border-2 border-amber-500"
        >
          <Scissors size={14} />
          {tx({ zh: '重新切分', en: 'Re-split' })}
        </button>
      </div>

      {/* 波形显示 */}
      {loading ? (
        <div className="h-24 bg-slate-50 rounded-2xl border-2 border-slate-100 flex items-center justify-center">
          <div className="flex items-center gap-2 text-slate-500">
            <div className="w-4 h-4 border-2 border-slate-300 border-t-emerald-500 rounded-full animate-spin" />
            <span className="text-xs font-bold">{tx({ zh: '加载波形...', en: 'Loading waveform…' })}</span>
          </div>
        </div>
      ) : (
        <div 
          ref={waveformRef}
          className="relative h-24 bg-slate-50 rounded-2xl border-2 border-slate-100 cursor-pointer overflow-hidden"
          onClick={handleWaveformClick}
        >
          {/* 波形条 */}
          <div className="absolute inset-0 flex items-center px-1">
            {waveform.map((peak, i) => (
              <div
                key={i}
                className="flex-1 mx-px bg-emerald-500/40 rounded-sm transition-all"
                style={{ height: `${Math.max(peak * 80, 2)}%` }}
              />
            ))}
          </div>
          
          {/* 段落切分区域 */}
          {segments?.map((seg, i) => {
            if (seg.start_time === undefined || seg.end_time === undefined) return null
            const left = (seg.start_time / duration) * 100
            const width = ((seg.end_time - seg.start_time) / duration) * 100
            const colors = [
              'bg-blue-500/20 border-blue-400',
              'bg-purple-500/20 border-purple-400',
              'bg-pink-500/20 border-pink-400',
              'bg-orange-500/20 border-orange-400',
              'bg-cyan-500/20 border-cyan-400',
              'bg-lime-500/20 border-lime-400',
              'bg-amber-500/20 border-amber-400',
              'bg-rose-500/20 border-rose-400',
              'bg-teal-500/20 border-teal-400',
            ]
            const colorClass = colors[i % colors.length]
            const borderColor = colorClass.split(' ')[1]
            const isDragging = draggingIndex === i
            return (
              <div
                key={i}
                className={`absolute top-0 bottom-0 ${colorClass.split(' ')[0]}`}
                style={{ left: `${left}%`, width: `${width}%` }}
              >
                {/* 左边界拖拽手柄 */}
                <div
                  className={`absolute top-0 bottom-0 left-0 w-1 ${borderColor.replace('border-', 'bg-')} cursor-ew-resize hover:w-2 transition-all ${isDragging ? 'w-2 opacity-100' : 'opacity-70'} ${i === 0 ? 'cursor-default opacity-30' : ''}`}
                  onMouseDown={(e) => handleDragStart(e, i)}
                  title={
                    i === 0
                      ? tx({ zh: '起始点不可移动', en: 'Start point cannot be moved' })
                      : tx({ zh: '拖拽调整 ({t})', en: 'Drag to adjust ({t})' }, { t: formatTime(seg.start_time) })
                  }
                />
                {/* 段落编号 */}
                <div className={`absolute top-1 left-2 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${borderColor.replace('border-', 'bg-')} text-white pointer-events-none`}>
                  {i + 1}
                </div>
                {/* 时间标签 */}
                <div className="absolute bottom-1 left-2 text-[9px] text-slate-600 font-mono font-bold pointer-events-none">
                  {formatTime(seg.start_time)}
                </div>
              </div>
            )
          })}
          
          {/* 播放位置 */}
          <div
            className="absolute top-0 bottom-0 w-0.5 bg-slate-700 shadow-lg"
            style={{ left: `${(currentTime / duration) * 100}%` }}
          />
        </div>
      )}

      {/* 播放控制 */}
      <div className="flex items-center gap-3 mt-3">
        <button
          onClick={togglePlay}
          className="p-2 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white transition-colors border-2 border-emerald-600"
        >
          {isPlaying ? <Pause size={16} /> : <Play size={16} />}
        </button>
        <span className="text-sm text-slate-700 font-mono font-bold">{formatTime(currentTime)}</span>
        <div className="flex-1" />
        <span className="text-[11px] text-slate-500 font-medium">
          {tx({ zh: '点击波形跳转 | 空格键播放/暂停', en: 'Click waveform to seek | Space to play/pause' })}
        </span>
      </div>
    </Card>
  )
}
